import { always, isNil, keys, reject } from 'ramda'
import ApiClient from 'api-client/ApiClient'
import { CreateDomain } from 'api-client/keystone.model'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import Bugsnag from 'utils/bugsnag'
import { emptyArr } from 'utils/fp'
import { Domain } from './model'

const { keystone } = ApiClient.getInstance()
export const getValidDomains = (domains) => {
  return domains.filter(
    (domain) => !(domain?.name === 'heat' && domain?.description === 'Service Domain for qe/heat'),
  )
}

export const domainsActions = ActionsSet.make<DataKeys.Domains>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.Domains,
  cacheKey: DataKeys.Domains,
})

export const listDomains = domainsActions.add(
  new ListAction<DataKeys.Domains>(async () => {
    Bugsnag.leaveBreadcrumb('Attempting to load domains')
    const domains = await keystone.getDomains()
    return getValidDomains(domains)
  }),
)

export const createDomain = domainsActions.add(
  new CreateAction<DataKeys.Domains>(async (body) => {
    Bugsnag.leaveBreadcrumb('Attempting to create domain')
    return keystone.createDomain(body as unknown as CreateDomain)
  }),
)

export const updateDomain = domainsActions.add(
  new UpdateAction<
    DataKeys.Domains,
    {
      id: string
      roleAssignments: unknown[]
      prevRoleAssignments: unknown[]
      body: any
    },
    any
  >(async ({ id, roleAssignments, prevRoleAssignments, body }) => {
    Bugsnag.leaveBreadcrumb('Attempting to update domain')

    const mergedUserIds = keys({ ...prevRoleAssignments, ...roleAssignments })

    // Perform the api calls to update the domain and the user/role assignments
    const updateDomainPromise = keystone.updateDomain(id as string, body as unknown as CreateDomain)

    const updateUserRolesPromises = mergedUserIds.map((userId) => {
      const prevRoleId = prevRoleAssignments[userId]
      const currRoleId = roleAssignments[userId]
      if (prevRoleId && !currRoleId) {
        // Remove unselected user/role pair
        return keystone
          .deleteDomainUserRole({ domainId: id, userId, roleId: prevRoleId })
          .then(always(null))
      } else if (!prevRoleId && currRoleId) {
        // Add new user and role
        return keystone.addDomainUserRole({ domainId: id, userId, roleId: currRoleId })
      } else if (prevRoleId && currRoleId && prevRoleId !== currRoleId) {
        // Update changed role (delete current and add new)
        return keystone
          .deleteDomainUserRole({ domainId: id, userId, roleId: prevRoleId })
          .then(() => keystone.addDomainUserRole({ domainId: id, userId, roleId: currRoleId }))
      }
      return Promise.resolve(null)
    }, [])
    // Resolve tenant and user/roles operation promises and filter out null responses.
    // Note: role-assignment failures are intentionally NOT swallowed here — if
    // addDomainUserRole/deleteDomainUserRole rejects, this Promise.all rejects too,
    // so the caller/UI can detect the partial failure even though the domain update
    // itself (updateDomainPromise) still runs to completion.
    const [updatedDomain] = await Promise.all([
      updateDomainPromise,
      Promise.all(updateUserRolesPromises).then(reject(isNil)),
    ])
    return updatedDomain
  }),
)

export const deleteDomain = domainsActions.add(
  new DeleteAction<DataKeys.Domains>(async (body) => {
    Bugsnag.leaveBreadcrumb('Attempting to delete domain')
    return keystone.deleteDomain(body as unknown as Domain)
  }),
)

export const getDomainRoleAssignments = async ({ domainId }) => {
  const result = await keystone.getDomainRoleAssignments(domainId)
  return result || emptyArr
}
